import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@/components/Common/Modal';
import { Button } from '@/components/Common/button';
import { Input } from '@/components/Common/input';
import { Label } from '@/components/Common/label';
import { Organization } from '@/types';
import organizationService from '@/services/organizationService';

export interface InviteUserFormData {
  email: string;
  organization_id: string;
}

interface InviteUserModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: InviteUserFormData) => Promise<void>;
  /** Prefill email (e.g. when inviting from Non-Sync "In DB, not in Logto") */
  initialEmail?: string | null;
}

export const InviteUserModal: React.FC<InviteUserModalProps> = ({ open, onClose, onSubmit, initialEmail }) => {
  const { t } = useTranslation();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [formData, setFormData] = useState<InviteUserFormData>({ email: '', organization_id: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setLoadingOrgs(true);
      organizationService
        .getOrganizations()
        .then(setOrganizations)
        .finally(() => setLoadingOrgs(false));
      setFormData({ email: initialEmail ?? '', organization_id: organizations[0]?.id ?? '' });
      setErrors({});
    }
  }, [open, initialEmail]);

  useEffect(() => {
    if (open && organizations.length) {
      setFormData((prev) => ({
        ...prev,
        organization_id: prev.organization_id || organizations[0].id,
        email: initialEmail ?? prev.email,
      }));
    }
  }, [open, organizations, initialEmail]);

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!formData.email.trim()) {
      next.email = t('admin.users.invite.errors.emailRequired', 'Email is required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      next.email = t('admin.users.invite.errors.emailInvalid', 'Invalid email format');
    }
    if (!formData.organization_id) {
      next.organization_id = t('admin.users.invite.errors.orgRequired', 'Organization is required');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await onSubmit(formData);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} title={t('admin.users.invite.title', 'Invite user')} onClose={onClose} hideDefaultFooter>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="invite-email">
            {t('admin.users.invite.emailLabel', 'Email')} *
          </Label>
          <Input
            id="invite-email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
            placeholder="user@example.com"
          />
          {errors.email && <p className="mt-1 text-sm text-destructive">{errors.email}</p>}
        </div>
        <div>
          <Label htmlFor="invite-org">
            {t('admin.users.invite.organizationLabel', 'Organization')} *
          </Label>
          <select
            id="invite-org"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={formData.organization_id}
            onChange={(e) => setFormData((prev) => ({ ...prev, organization_id: e.target.value }))}
            disabled={loadingOrgs}
          >
            <option value="">{loadingOrgs ? '...' : t('admin.users.invite.selectOrg', 'Select organization')}</option>
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
          {errors.organization_id && (
            <p className="mt-1 text-sm text-destructive">{errors.organization_id}</p>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            {t('global.cancel')}
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? t('global.sending', 'Sending...') : t('admin.users.invite.submit', 'Send invitation')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
